/**
 * Employee-facing widget showing the list of advances the employee has received,
 * with a button to:
 *   - View / download / sign each receipt
 *   - Submit a new advance request to HR
 *
 * Mounted on EmployeePortal.js next to documents.
 */
import { useEffect, useState } from "react";
import api from "../lib/api";
import { fmtEur } from "../lib/format";
import { AdvancePdfPreviewDialog } from "./AdvancePdfPreviewDialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
    Wallet,
    Plus,
    Download,
    FileSignature,
    Loader2,
    CheckCircle2,
    Clock,
    AlertCircle,
    Euro,
    Eye,
} from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = {
    ACCONTO: "Acconto", FNG: "F.N.G.", TFR: "T.F.R.", PRESTITO: "Prestito",
    ANF: "A.N.F.", EMOLUMENTI: "Emolumenti",
    ACCONTO_FERIE_NON_GODUTE: "Acconto ferie non godute",
    ACCONTO_TFR: "Acconto T.F.R.",
};

const STATUS_BADGES = {
    issued: { label: "Da firmare", color: "bg-amber-50 text-amber-700 border-amber-200", icon: AlertCircle },
    signed: { label: "Firmato", color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
    recovered: { label: "Recuperato", color: "bg-slate-100 text-slate-600 border-slate-300", icon: CheckCircle2 },
    cancelled: { label: "Annullato", color: "bg-red-50 text-red-700 border-red-200", icon: AlertCircle },
};

const REQUEST_STATUS = {
    pending: { label: "In attesa", color: "bg-amber-50 text-amber-700 border-amber-200" },
    approved: { label: "Approvata", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    rejected: { label: "Rifiutata", color: "bg-red-50 text-red-700 border-red-200" },
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
    catch { return iso; }
};


export function EmployeeAdvancesCard({ onStatsChange, defaultOpen = true, canRequest = true } = {}) {
    const [advances, setAdvances] = useState([]);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [requestOpen, setRequestOpen] = useState(false);
    const [signOpen, setSignOpen] = useState(null);
    const [tfrSignOpen, setTfrSignOpen] = useState(null);
    const [previewAdvance, setPreviewAdvance] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [a, r] = await Promise.all([
                api.get("/advances", { params: { mine: 1 } }),
                api.get("/advance-requests", { params: { mine: 1 } }),
            ]);
            setAdvances(a.data);
            setRequests(r.data);
        } catch (e) {
            /* ignore */
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    // Notify parent of count and unsigned status whenever data changes
    useEffect(() => {
        if (!onStatsChange || loading) return;
        const hasNew = advances.some(
            (a) => (a.status === "issued" || a.status === "recovered") && !a.signed_at,
        ) || requests.some((r) => r.status === "pending");
        onStatsChange({ count: advances.length, hasNew });
    }, [advances, requests, loading, onStatsChange]);

    const total = advances.reduce((s, a) => s + (a.amount || 0), 0);
    const toSign = advances.filter((a) => a.status === "issued" || a.status === "recovered" && !a.signed_at).length;

    if (loading) {
        return (
            <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
            </section>
        );
    }

    return (
        <section
            className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
            data-testid="employee-advances-card"
        >
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-gradient-to-br from-[#5BAEDB]/10 to-[#376DB3]/10 px-5 py-4">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#376DB3] text-white">
                        <Wallet className="h-5 w-5" />
                    </div>
                    <div>
                        <h3 className="font-display text-lg font-bold text-[#15498F]">I miei acconti</h3>
                        <p className="text-xs text-slate-600">
                            {advances.length} ricevut{advances.length === 1 ? "a" : "e"} · totale {fmtEur(total)}
                            {toSign > 0 && (
                                <span className="ml-2 inline-flex items-center gap-0.5 text-amber-700">
                                    <AlertCircle className="h-3 w-3" /> {toSign} da firmare
                                </span>
                            )}
                        </p>
                    </div>
                </div>
                {canRequest && (
                <Button
                    onClick={() => setRequestOpen(true)}
                    className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                    data-testid="emp-request-advance-btn"
                >
                    <Plus className="h-4 w-4" />
                    Richiedi acconto
                </Button>
                )}
            </div>

            <div className="px-5 py-4">
                {/* Pending requests */}
                {requests.length > 0 && (
                    <div className="mb-4">
                        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Le mie richieste
                        </h4>
                        <div className="space-y-2">
                            {requests.slice(0, 4).map((r) => {
                                const cfg = REQUEST_STATUS[r.status] || REQUEST_STATUS.pending;
                                return (
                                    <div
                                        key={r.id}
                                        className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-sm"
                                        data-testid={`emp-request-${r.id}`}
                                    >
                                        <div>
                                            <span className="font-medium text-slate-800">{fmtEur(r.amount)}</span>
                                            <span className="ml-2 text-xs text-slate-500">{fmtDate(r.requested_at)}</span>
                                            {r.reason && (
                                                <div className="text-xs italic text-slate-500">{r.reason}</div>
                                            )}
                                            {r.recovery_target && (
                                                <div className="text-xs text-slate-500">
                                                    Recupero: {r.recovery_target === "FNG" ? "Ferie non godute"
                                                        : r.recovery_target === "TFR" ? "T.F.R."
                                                        : `busta paga ${r.recovery_month || ""}${(r.recovery_months_count || 1) > 1 ? ` (${r.recovery_months_count} mensilità)` : ""}`}
                                                </div>
                                            )}
                                            {r.admin_note && (
                                                <div className="mt-0.5 text-xs text-slate-600">Nota HR: {r.admin_note}</div>
                                            )}
                                        </div>
                                        <Badge variant="outline" className={`text-xs ${cfg.color}`}>
                                            {cfg.label}
                                        </Badge>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {advances.length === 0 ? (
                    <p className="py-6 text-center text-sm text-slate-500">
                        Non hai ancora ricevute di acconti.
                    </p>
                ) : (
                    <div className="space-y-2">
                        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Ricevute
                        </h4>
                        {advances.map((a) => {
                            const cfg = STATUS_BADGES[a.status] || STATUS_BADGES.issued;
                            const StatusIcon = cfg.icon;
                            const needsSignature = a.status === "issued" && !a.signed_at;
                            return (
                                <div
                                    key={a.id}
                                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2.5 hover:bg-slate-50"
                                    data-testid={`emp-advance-${a.id}`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-xs font-semibold text-[#376DB3]">
                                                #{a.receipt_label}
                                            </span>
                                            <span className="font-medium text-slate-800">
                                                {fmtEur(a.amount)}
                                            </span>
                                            <span className="text-xs text-slate-500">
                                                {fmtDate(a.date)} · {TYPE_LABELS[a.type] || a.type}
                                            </span>
                                        </div>
                                        {a.recovery_payslip && (
                                            <div className="mt-0.5 text-xs text-slate-500">
                                                Recupero: <span className="font-medium">{a.recovery_payslip}</span>
                                                {a.recovery_rates > 1 && <span> in {a.recovery_rates} rate</span>}
                                            </div>
                                        )}
                                        {["TFR", "ACCONTO_TFR"].includes(a.type) && (
                                            <div className="mt-1.5 flex flex-wrap items-center gap-1.5 border-t border-dashed border-slate-200 pt-1.5">
                                                <span className="text-[11px] font-medium text-slate-600">Dichiarazione T.F.R. (art. 2120 c.c.):</span>
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-6 gap-1 px-1.5 text-[11px]"
                                                    onClick={async () => {
                                                        try {
                                                            const r = await api.get(`/advances/${a.id}/tfr-declaration/pdf`, { responseType: "blob" });
                                                            const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                                                            window.open(url, "_blank");
                                                        } catch {
                                                            toast.error("Errore apertura dichiarazione");
                                                        }
                                                    }}
                                                    data-testid={`emp-tfr-decl-pdf-${a.id}`}
                                                >
                                                    <Eye className="h-3 w-3" /> Vedi
                                                </Button>
                                                {a.tfr_decl_signed_at ? (
                                                    <Badge variant="outline" className="gap-1 border-emerald-200 bg-emerald-50 px-1.5 text-[10px] text-emerald-700" data-testid={`emp-tfr-decl-signed-${a.id}`}>
                                                        <CheckCircle2 className="h-3 w-3" /> Firmata il {fmtDate(a.tfr_decl_signed_at)}
                                                    </Badge>
                                                ) : (
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        className="h-6 gap-1 bg-emerald-600 px-2 text-[11px] text-white hover:bg-emerald-700"
                                                        onClick={() => setTfrSignOpen(a)}
                                                        data-testid={`emp-tfr-decl-sign-${a.id}`}
                                                    >
                                                        <FileSignature className="h-3 w-3" /> Firma dichiarazione
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <Badge variant="outline" className={`gap-1 text-xs ${cfg.color}`}>
                                            <StatusIcon className="h-3 w-3" />
                                            {cfg.label}
                                        </Badge>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="ghost"
                                            className="gap-1 text-xs"
                                            onClick={() => setPreviewAdvance(a)}
                                            data-testid={`emp-advance-pdf-${a.id}`}
                                            title="Anteprima ricevuta"
                                        >
                                            <Eye className="h-3 w-3" />
                                        </Button>
                                        {needsSignature && (
                                            <Button
                                                type="button"
                                                size="sm"
                                                className="gap-1 bg-emerald-600 text-white hover:bg-emerald-700"
                                                onClick={() => setSignOpen(a)}
                                                data-testid={`emp-advance-sign-${a.id}`}
                                            >
                                                <FileSignature className="h-3 w-3" />
                                                Firma
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <RequestAdvanceDialog
                open={requestOpen}
                onClose={() => setRequestOpen(false)}
                onCreated={() => { setRequestOpen(false); fetchData(); }}
            />
            <SignAdvanceDialog
                advance={signOpen}
                onClose={() => setSignOpen(null)}
                onSigned={() => { setSignOpen(null); fetchData(); }}
            />
            <SignTfrDeclDialog
                advance={tfrSignOpen}
                onClose={() => setTfrSignOpen(null)}
                onSigned={() => { setTfrSignOpen(null); fetchData(); }}
            />
            <AdvancePdfPreviewDialog
                advance={previewAdvance}
                open={!!previewAdvance}
                onClose={() => setPreviewAdvance(null)}
            />
        </section>
    );
}


function RequestAdvanceDialog({ open, onClose, onCreated }) {
    const [amount, setAmount] = useState("");
    const [reason, setReason] = useState("");
    const [target, setTarget] = useState("BUSTA_PAGA");
    const [month, setMonth] = useState("");
    const [monthsCount, setMonthsCount] = useState(1);
    const [submitting, setSubmitting] = useState(false);

    const monthOptions = (() => {
        const opts = [];
        const d = new Date();
        const names = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
            "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
        for (let i = 0; i < 12; i++) {
            const m = new Date(d.getFullYear(), d.getMonth() + i, 1);
            const value = `${String(m.getMonth() + 1).padStart(2, "0")}/${m.getFullYear()}`;
            opts.push({ value, label: `${names[m.getMonth()]} ${m.getFullYear()}` });
        }
        return opts;
    })();

    useEffect(() => {
        if (open) {
            setAmount(""); setReason(""); setTarget("BUSTA_PAGA");
            setMonth(""); setMonthsCount(1);
        }
    }, [open]);

    const submit = async (e) => {
        e.preventDefault();
        if (!amount || parseFloat(amount) <= 0) { toast.error("Importo non valido"); return; }
        if (!reason || reason.trim().length < 3) { toast.error("Inserisci una motivazione"); return; }
        if (target === "BUSTA_PAGA" && !month) { toast.error("Scegli la busta paga di riferimento"); return; }
        setSubmitting(true);
        try {
            await api.post("/advance-requests", {
                amount: parseFloat(amount),
                reason: reason.trim(),
                recovery_target: target,
                recovery_month: target === "BUSTA_PAGA" ? month : null,
                recovery_months_count: target === "BUSTA_PAGA" ? parseInt(monthsCount, 10) || 1 : 1,
            });
            toast.success("Richiesta inviata. L'ufficio HR la valuterà.");
            onCreated();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore invio richiesta");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="request-advance-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display text-[#15498F]">
                        <Euro className="h-5 w-5" />
                        Richiedi un acconto
                    </DialogTitle>
                    <DialogDescription>
                        L'ufficio HR riceverà la richiesta e ti contatterà per l'approvazione.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <Label className="text-xs">Importo (€) *</Label>
                        <Input
                            type="number"
                            step="0.01"
                            min="1"
                            max="10000"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder="es. 300.00"
                            className="mt-1"
                            required
                            data-testid="request-amount"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Motivazione *</Label>
                        <Input
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="es. Spesa medica urgente"
                            className="mt-1"
                            required
                            minLength={3}
                            data-testid="request-reason"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Dove recuperare l'acconto? *</Label>
                        <div className="mt-1 grid grid-cols-3 gap-1.5" data-testid="request-target-group">
                            {[
                                { value: "BUSTA_PAGA", label: "Busta paga" },
                                { value: "FNG", label: "Ferie non godute" },
                                { value: "TFR", label: "T.F.R." },
                            ].map((o) => (
                                <button
                                    key={o.value}
                                    type="button"
                                    onClick={() => setTarget(o.value)}
                                    className={`rounded-md border px-2 py-2 text-xs font-medium transition-colors ${
                                        target === o.value
                                            ? "border-[#15498F] bg-[#15498F] text-white"
                                            : "border-slate-200 bg-white text-slate-600 hover:border-[#15498F]"
                                    }`}
                                    data-testid={`request-target-${o.value}`}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    {target === "BUSTA_PAGA" && (
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <Label className="text-xs">Busta paga di riferimento *</Label>
                                <select
                                    value={month}
                                    onChange={(e) => setMonth(e.target.value)}
                                    className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
                                    data-testid="request-month-select"
                                >
                                    <option value="">Scegli il mese…</option>
                                    {monthOptions.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <Label className="text-xs">In quante mensilità?</Label>
                                <select
                                    value={monthsCount}
                                    onChange={(e) => setMonthsCount(e.target.value)}
                                    className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
                                    data-testid="request-months-count-select"
                                >
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                                        <option key={n} value={n}>{n === 1 ? "1 mensilità" : `${n} mensilità`}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
                        <Button
                            type="submit"
                            disabled={submitting}
                            className="gap-2 bg-[#15498F] text-white hover:bg-[#15498F]/90"
                            data-testid="request-submit-btn"
                        >
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                            Invia richiesta
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}


const ACK_TEXT_TEMPLATE = (advance) => (
    `Il/La sottoscritto/a dichiara di aver ricevuto in data ${advance ? new Date(advance.date).toLocaleDateString("it-IT") : "—"} ` +
    `la somma di € ${(advance?.amount || 0).toFixed(2).replace(".", ",")} ` +
    `a titolo di "${TYPE_LABELS[advance?.type] || advance?.type}", ` +
    `da recuperare ${advance?.recovery_payslip ? `sulla busta paga ${advance.recovery_payslip}` : "secondo le modalità concordate"}` +
    `${advance?.recovery_rates > 1 ? ` in ${advance.recovery_rates} rate mensili` : ""}.\n\n` +
    `Confermo l'esattezza dei dati e l'accettazione delle condizioni di recupero. ` +
    `La presente firma digitale costituisce equivalente legale della firma autografa ai sensi ` +
    `del Regolamento UE 910/2014 (eIDAS), art. 25.`
);


const TFR_DECL_ACK_TEXT = (advance) => (
    `Il/La sottoscritto/a dichiara di aver richiesto e ricevuto in data ${advance ? new Date(advance.date).toLocaleDateString("it-IT") : "—"} ` +
    `la somma di € ${(advance?.amount || 0).toFixed(2).replace(".", ",")} a titolo di anticipazione sul T.F.R. maturato, ` +
    `ai sensi dell'art. 2120 del Codice Civile, come da ricevuta n. ${advance?.receipt_label || "—"}.\n\n` +
    `Dichiara di essere consapevole e di accettare che tale somma sarà detratta a tutti gli effetti dal trattamento di fine rapporto maturato ` +
    `e conteggiata in sede di liquidazione finale del T.F.R., all'atto della cessazione del rapporto di lavoro.\n\n` +
    `La presente firma digitale costituisce equivalente legale della firma autografa ai sensi del Regolamento UE 910/2014 (eIDAS), art. 25.`
);


function SignTfrDeclDialog({ advance, onClose, onSigned }) {
    const [accepted, setAccepted] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (advance) setAccepted(false);
    }, [advance]);

    const submit = async () => {
        if (!accepted || !advance) return;
        setSubmitting(true);
        try {
            await api.post(`/advances/${advance.id}/tfr-declaration/sign`, { ack_text: TFR_DECL_ACK_TEXT(advance) });
            toast.success("Dichiarazione TFR firmata digitalmente");
            onSigned();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore firma");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={!!advance} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-xl" data-testid="sign-tfr-decl-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display text-[#15498F]">
                        <FileSignature className="h-5 w-5" />
                        Firma dichiarazione T.F.R. {advance && `— ricevuta #${advance.receipt_label}`}
                    </DialogTitle>
                </DialogHeader>
                {advance && (
                    <>
                        <div className="rounded-md border border-[#5BAEDB]/30 bg-[#5BAEDB]/5 p-4 text-sm leading-relaxed text-slate-700">
                            {TFR_DECL_ACK_TEXT(advance).split("\n\n").map((p, i) => (
                                <p key={p} className={i > 0 ? "mt-3" : ""}>{p}</p>
                            ))}
                        </div>
                        <label className="flex items-start gap-2 text-sm text-slate-700">
                            <input
                                type="checkbox"
                                checked={accepted}
                                onChange={(e) => setAccepted(e.target.checked)}
                                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#376DB3] focus:ring-[#376DB3]"
                                data-testid="sign-tfr-checkbox"
                            />
                            <span>
                                Ho letto e accetto. <strong>Confermo la firma digitale</strong> di questa dichiarazione.
                            </span>
                        </label>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
                            <Button
                                onClick={submit}
                                disabled={!accepted || submitting}
                                className="gap-2 bg-emerald-600 text-white hover:bg-emerald-700"
                                data-testid="sign-tfr-submit-btn"
                            >
                                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                                Firma adesso
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}


function SignAdvanceDialog({ advance, onClose, onSigned }) {
    const [accepted, setAccepted] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (advance) setAccepted(false);
    }, [advance]);

    const submit = async () => {
        if (!accepted || !advance) return;
        setSubmitting(true);
        try {
            const ackText = ACK_TEXT_TEMPLATE(advance);
            await api.post(`/advances/${advance.id}/sign`, { ack_text: ackText });
            toast.success("Ricevuta firmata digitalmente");
            onSigned();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore firma");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={!!advance} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-xl" data-testid="sign-advance-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display text-[#15498F]">
                        <FileSignature className="h-5 w-5" />
                        Firma digitale ricevuta {advance && `#${advance.receipt_label}`}
                    </DialogTitle>
                </DialogHeader>
                {advance && (
                    <>
                        <div className="rounded-md border border-[#5BAEDB]/30 bg-[#5BAEDB]/5 p-4 text-sm leading-relaxed text-slate-700">
                            {ACK_TEXT_TEMPLATE(advance).split("\n\n").map((p, i) => (
                                <p key={p} className={i > 0 ? "mt-3" : ""}>{p}</p>
                            ))}
                        </div>
                        <label className="flex items-start gap-2 text-sm text-slate-700">
                            <input
                                type="checkbox"
                                checked={accepted}
                                onChange={(e) => setAccepted(e.target.checked)}
                                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#376DB3] focus:ring-[#376DB3]"
                                data-testid="sign-checkbox"
                            />
                            <span>
                                Ho letto e accetto. <strong>Confermo la firma digitale</strong> di questa ricevuta.
                            </span>
                        </label>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
                            <Button
                                onClick={submit}
                                disabled={!accepted || submitting}
                                className="gap-2 bg-emerald-600 text-white hover:bg-emerald-700"
                                data-testid="sign-submit-btn"
                            >
                                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                                Firma adesso
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
